import React, { useContext } from "react"
import styled from "styled-components"
import {
  containerMaxWidth,
  MEDIA_MIN_MEDIUM,
  MEDIA_MIN_LARGE
} from "../../constants"
import KlarnaLogo from "../../icons/usp/klarna-logo.svg"
import SafeHomeDelivery from "../../icons/usp/safe-home-delivery.svg"
import SustainableMaterials from "../../icons/usp/sustainable-materials.svg"
import HardCaseIncluded from "../../icons/usp/hard-case-included.svg"
import InspiredBy70s from "../../icons/usp/inspired-by-70s.svg"
import Exchange365 from "../../icons/usp/exchange-365.svg"
import { BrinkContext } from "../context/BrinkContext"

const Wrapper = styled.div`
  width: 100%;
  max-width: ${(p) => (p.containToGrid ? containerMaxWidth : "100%")};
  margin: 0 auto 4rem;
  display: flex;
  justify-content: center;
  background: ${(p) => p.backgroundColor || "transparent"};
`

const Container = styled.div`
  display: flex;
  margin: ${(p) => (!p.isOnProductPage ? "0 auto" : "0")};
  max-width: 120rem;
  width: 100%;
  padding: ${(p) => (!p.isOnProductPage ? "0 1rem" : "0")};
  flex-direction: column;

  ${MEDIA_MIN_MEDIUM} {
    flex-direction: row;
  }

  ${MEDIA_MIN_LARGE} {
    padding: 0;
    margin: ${(p) => (!p.isOnProductPage ? "2.2rem auto 2rem" : "0")};
  }
`

const UspContainer = styled.div`
  height 100%;
  display: flex;
  align-items: center;
  padding: 2rem 1.5rem;
  width: 100%;
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.lightBorder};

  &:last-child {
    border: none;
  }

  ${MEDIA_MIN_MEDIUM} {
    flex: 1;
    flex-direction: column;
    border: none;
  }

  ${MEDIA_MIN_LARGE} {
    padding: 0.6rem 2rem 1rem;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const UspTitle = styled.h3`
  font-size: 1.5rem;
  letter-spacing: 0.2rem;
  line-height: 1.8rem;
  margin: 0 2rem 0.2rem;
  color: ${(p) => p.textColor || p.theme.colors.black};

  ${MEDIA_MIN_MEDIUM} {
    max-width: 11rem;
    margin: 0.5rem auto 0.5rem;
    text-align: center;
  }

  ${MEDIA_MIN_LARGE} {
    max-width: 25rem;
    font-size: 1.4rem;
    line-height: 2rem;
  }
`

const UspText = styled.p`
  margin: 0 0 0 2rem;
  color: ${(p) => p.textColor || p.theme.colors.black};

  ${MEDIA_MIN_MEDIUM} {
    max-width: 11rem;
    margin: 0 auto;
    text-align: center;
    line-height: 2.2rem;
  }

  ${MEDIA_MIN_LARGE} {
    max-width: 25rem;
  }
`

const IconContainer = styled.div`
  height: 4rem;
  width: 4rem;
  border: 0.1rem solid ${(p) => p.textColor || p.theme.colors.black};
  margin-bottom: 1rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;

  i {
    font-size: 2rem;
    color: ${(p) => p.textColor || p.theme.colors.black};
  }

  ${MEDIA_MIN_LARGE} {
    height: 5rem;
    width: 5rem;
    border-radius: 2.5rem;

    i {
      font-size: 2.4rem;
    }
  }

  svg {
    width: 5.5rem;
    margin-bottom: 0.4rem;
    fill: ${(p) => p.textColor || p.theme.colors.secondary};

    ${MEDIA_MIN_LARGE} {
      margin-bottom: 1rem;
      width: 7rem;
    }
  }
`

const KlarnaIcon = styled(KlarnaLogo)`
  width: 6rem;
  margin-bottom: 0.6rem;

  ${MEDIA_MIN_LARGE} {
    width: 8rem !important;
  }
`

const Exchange365Icon = styled(Exchange365)`
  width: 2.5rem !important;
  margin-bottom: 0.6rem !important;

  ${MEDIA_MIN_LARGE} {
    width: 3rem !important;
  }
`

const getIcon = (usp) => {
  switch (usp.uspIcon) {
    case "klarna":
      return <KlarnaIcon />
    case "safe-home-delivery":
      return <SafeHomeDelivery />
    case "sustainable-materials":
      return <SustainableMaterials />
    case "hard-case-included":
      return <HardCaseIncluded />
    case "inspired-by-70s":
      return <InspiredBy70s />
    case "365":
      return <Exchange365Icon />

    default:
      return <i className={`fal fa-${usp.uspIcon}`}></i>
  }
}

const Usp = ({ data, isOnProductPage }) => {
  const { languageCode } = useContext(BrinkContext)
  const { containToGrid, backgroundColor, textColor, iconColor, uspEntries } =
    data

  return (
    <Wrapper containToGrid={containToGrid} backgroundColor={backgroundColor}>
      <Container isOnProductPage={isOnProductPage}>
        {uspEntries.map((usp) => (
          <UspContainer key={usp.uspText.en}>
            <IconContainer textColor={iconColor}>{getIcon(usp)}</IconContainer>
            <TextContainer>
              <UspTitle textColor={textColor}>
                {usp.uspTitle[languageCode] || usp.uspTitle.en}
              </UspTitle>
              <UspText textColor={textColor}>
                {usp.uspText[languageCode] || usp.uspText.en}
              </UspText>
            </TextContainer>
          </UspContainer>
        ))}
      </Container>
    </Wrapper>
  )
}

export default Usp
